import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Auth, signOut, User } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

export enum UserStatus {
  UNLOGGED = "UNLOGGED",
  ADMIN = "ADMIN",
  SUPER = "SUPER",
  UNAUTHORIZED = "UNAUTHORIZED"
}

interface UserClaims {
  admin?: boolean;
  super?: boolean;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private _userStatus: UserStatus = UserStatus.UNLOGGED;
  private claims: UserClaims = {};
  private _userName = '';
  private _userEmail = '';
  private _userPhotoUrl = '';

  private initializationPromise!: Promise<void>;
  private resolveInitialization!: () => void;
  uid$ = new BehaviorSubject("");

  constructor(
    public auth: Auth,
    private router: Router,
    private _snackBar: MatSnackBar
  ) {
    this.initializationPromise = new Promise<void>((resolve) => {
      this.resolveInitialization = resolve;

      // Security timeout to ensure initialization doesn't hang indefinitely
      setTimeout(() => {
        if (this.resolveInitialization) {
          console.warn('Authorization timeout during initialization: unknown status.');
          this.resolveInitialization();
          this.resolveInitialization = null!;
        }
      }, 5000); // 5s timeout
    });

    // Listen for authentication token changes
    this.auth.onIdTokenChanged(async (user: User | null) => {
      if (user) {
        await this.setUserData(user);
        this.uid$.next(user.uid);
      } else {
        this.resetUserData();
        this.uid$.next("");
      }

      // Resolve initialization promise only once
      if (this.resolveInitialization) {
        this.resolveInitialization();
        this.resolveInitialization = null!;
      }
    });
  }

  // Set user data from authentication token
  private async setUserData(user: User): Promise<void> {
    const tokenResult = await user.getIdTokenResult();

    this._userStatus = user.email === "zornguido@gmail.com"
      ? UserStatus.ADMIN
      : UserStatus.UNAUTHORIZED;

    this._userName = user.displayName ?? '';
    this._userEmail = user.email ?? '';
    this._userPhotoUrl = user.photoURL ?? '';
  }

  // Reset user data to default values
  private resetUserData(): void {
    this.claims = {};
    this._userStatus = UserStatus.UNLOGGED;
    this._userName = '';
    this._userEmail = '';
    this._userPhotoUrl = '';
  }

  // Return initialization promise
  public get isInitialized(): Promise<void> {
    return this.initializationPromise;
  }

  // Check if user is authenticated
  public get isAuthenticated(): boolean {
    return this._userStatus !== UserStatus.UNLOGGED;
  }

  // Get user name
  public get userName(): string {
    return this._userName;
  }

  // Get user email
  public get userEmail(): string {
    return this._userEmail;
  }

  // Get user photo URL
  public get userPhotoUrl(): string {
    return this._userPhotoUrl;
  }

  // Get user status
  public get userStatus(): UserStatus {
    return this._userStatus;
  }

  // Check if user has admin privileges
  public get userIsAdmin(): boolean {
    return this._userStatus === UserStatus.ADMIN ;
  }


  // Check if user is unauthorized
  public get userIsUnauthorized(): boolean {
    return this._userStatus === UserStatus.UNAUTHORIZED;
  }

  // Logout user and reset state
  public logout(): void {
    signOut(this.auth)
      .then(() => {
        this.resetUserData();
        this._snackBar.open("Successfully logged out", undefined, { duration: 3000 });
        this.router.navigateByUrl("/");
      })
      .catch((err) => {
        console.error("Error during logout:", err);
        this._snackBar.open("Error during logout. Please try again.", undefined, { duration: 3000 });
      });
  }
}